import type { HttpClient } from '@shared/http/http-client';
import type { ClientPortalArtifact } from '@domain/models/client-portal-artifact.interface';
import type { LabPortalBranding } from '@domain/models/lab-portal-branding.interface';
import type {
  DeliveryListQuery,
  IClientPortalRepository,
} from '@domain/data-contracts/client-portal-repository.interface';
import type { InvoiceListQuery } from '@domain/data-contracts/invoice-repository.interface';
import type { PatientListQuery } from '@domain/data-contracts/patient-repository.interface';
import type { ClientInvoice } from '@domain/models/client-invoice.interface';
import type { ClientOrder } from '@domain/models/client-order.interface';
import type { ClientTimelineStep } from '@domain/models/client-timeline-step.interface';
import type { DeliveryNote } from '@domain/models/delivery-note.interface';
import type { NewOrderDraft } from '@domain/models/new-order-draft.interface';
import type { OrderListQuery, PagedResult } from '@domain/models/paging.interface';
import type { Patient } from '@domain/models/patient.interface';
import type { PracticeProfile } from '@domain/models/practice-profile.interface';
import { ScannerStatus, type Scanner } from '@domain/models/scanner.interface';
import {
  mapClientInvoiceDto,
  mapClientOrderDto,
  mapClientTimelineStepDto,
  mapClientPortalDeliveryNoteDto,
  mapPatientDto,
  mapPaged,
  type ClientInvoiceDto,
  type ClientOrderDto,
  type ClientTimelineStepDto,
  type ClientPortalDeliveryNoteDto,
  type PatientDto,
  type PagedDto,
} from './mapping.helper';

const BASE = '/api/client';

/**
 * Single entry point for every client-portal HTTP call. The
 * dedicated `/api/client/*` endpoints guarantee the back-end
 * projects to the anonymised shapes — this class in turn maps
 * those DTOs into view-models through
 * `@data/api-repositories/mapping.helper.ts`.
 */
export class ClientPortalRepository implements IClientPortalRepository {
  public constructor(private readonly http: HttpClient) {}

  public async getPortalBranding(): Promise<LabPortalBranding> {
    const response = await this.http.get<LabPortalBranding>(`${BASE}/branding`);
    return response.data;
  }

  public async listOrders(query: OrderListQuery): Promise<PagedResult<ClientOrder>> {
    const response = await this.http.get<PagedDto<ClientOrderDto>>(`${BASE}/orders`, {
      params: pruneOrderParams(query),
    });
    return mapPaged(response.data, mapClientOrderDto);
  }

  public async getOrderById(id: string): Promise<ClientOrder> {
    const response = await this.http.get<ClientOrderDto>(
      `${BASE}/orders/${encodeURIComponent(id)}`,
    );
    return mapClientOrderDto(response.data);
  }

  public async getOrderTimeline(orderId: string): Promise<readonly ClientTimelineStep[]> {
    const response = await this.http.get<readonly ClientTimelineStepDto[]>(
      `${BASE}/orders/${encodeURIComponent(orderId)}/timeline`,
    );
    return response.data.map(mapClientTimelineStepDto);
  }

  public async listOrderAttachments(orderId: string): Promise<readonly ClientPortalArtifact[]> {
    const response = await this.http.get<readonly ClientPortalArtifactDto[]>(
      `${BASE}/orders/${encodeURIComponent(orderId)}/attachments`,
    );
    return response.data.map((artifact) => ({
      id: String(artifact.designRequestId ?? artifact.attachmentId),
      kind: artifact.kind,
      fileName: artifact.fileName,
      contentType: artifact.contentType,
      description: artifact.description,
      uploadedAt: artifact.uploadedDate,
      version: artifact.version,
      reviewStatus: artifact.reviewStatus,
      contentUrl: artifact.contentUrl,
      designRequestId:
        artifact.designRequestId === null ? null : String(artifact.designRequestId),
    }));
  }

  public async getArtifactBlob(contentUrl: string): Promise<Blob> {
    const response = await this.http.get<Blob>(contentUrl, { responseType: 'blob' });
    return response.data;
  }

  public async reviewDesign(
    orderId: string,
    designRequestId: string,
    decision: 'Approved' | 'RevisionRequested',
    note?: string,
  ): Promise<void> {
    await this.http.post(
      `${BASE}/orders/${encodeURIComponent(orderId)}/designs/${encodeURIComponent(designRequestId)}/review`,
      { decision, note },
    );
  }

  public async listInvoices(query: InvoiceListQuery): Promise<PagedResult<ClientInvoice>> {
    const response = await this.http.get<PagedDto<ClientInvoiceDto>>(`${BASE}/invoices`, {
      params: pruneInvoiceParams(query),
    });
    return mapPaged(response.data, mapClientInvoiceDto);
  }

  public async getInvoicePdfBlob(id: string): Promise<Blob> {
    const response = await this.http.get<Blob>(
      `${BASE}/invoices/${encodeURIComponent(id)}/pdf`,
      { responseType: 'blob' },
    );
    return response.data;
  }

  public async listDeliveries(query: DeliveryListQuery): Promise<PagedResult<DeliveryNote>> {
    const response = await this.http.get<PagedDto<ClientPortalDeliveryNoteDto>>(`${BASE}/deliveries`, {
      params: pruneDeliveryParams(query),
    });
    return mapPaged(response.data, mapClientPortalDeliveryNoteDto);
  }

  public async getDeliveryPdfBlob(id: string): Promise<Blob> {
    const response = await this.http.get<Blob>(
      `${BASE}/deliverynote/${encodeURIComponent(id)}/pdf`,
      { responseType: 'blob' },
    );
    return response.data;
  }

  public async submitOrder(draft: NewOrderDraft): Promise<ClientOrder> {
    const response = await this.http.post<ClientOrderDto>(`${BASE}/orders`, draft);
    return mapClientOrderDto(response.data);
  }

  public async listScanners(): Promise<readonly Scanner[]> {
    const response = await this.http.get<readonly ScannerDto[]>(`${BASE}/scanners`);
    return response.data.map(mapScannerDto);
  }

  public async getPracticeProfile(): Promise<PracticeProfile> {
    const response = await this.http.get<PracticeProfileDto>(`${BASE}/profile`);
    return {
      practiceName: response.data.practiceName,
      tier: response.data.tier,
      dentistLastName: response.data.dentistLastName,
    };
  }

  public async listPatients(query: PatientListQuery): Promise<PagedResult<Patient>> {
    const response = await this.http.get<PagedDto<PatientDto>>(`${BASE}/patients`, {
      params: prunePatientParams(query),
    });
    return mapPaged(response.data, mapPatientDto);
  }
}

interface ScannerDto {
  readonly id: string;
  readonly name: string;
  readonly location: string;
  readonly status: string;
  readonly offlineSinceMinutes: number | null;
}

interface ClientPortalArtifactDto {
  readonly attachmentId: number | null;
  readonly designRequestId: number | null;
  readonly kind: 'Scan' | 'Design' | 'Attachment';
  readonly fileName: string;
  readonly contentType: string | null;
  readonly description: string | null;
  readonly uploadedDate: string;
  readonly version: number | null;
  readonly reviewStatus: 'AwaitingReview' | 'Approved' | 'RevisionRequested' | null;
  readonly contentUrl: string;
}

interface PracticeProfileDto {
  readonly practiceName: string;
  readonly tier: string;
  readonly dentistLastName: string;
}

function mapScannerDto(dto: ScannerDto): Scanner {
  const raw = dto.status.toLowerCase();
  const status: Scanner['status'] =
    raw === ScannerStatus.Online ? ScannerStatus.Online : ScannerStatus.Offline;
  return {
    id: dto.id,
    name: dto.name,
    location: dto.location,
    status,
    offlineSinceMinutes: status === ScannerStatus.Offline ? dto.offlineSinceMinutes : null,
  };
}

function pruneOrderParams(query: OrderListQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (query.status) { out.status = query.status; }
  if (query.search && query.search.trim().length > 0) { out.search = query.search.trim(); }
  if (query.dueBefore) { out.dueBefore = query.dueBefore; }
  if (typeof query.page === 'number') { out.page = query.page; }
  if (typeof query.pageSize === 'number') { out.pageSize = query.pageSize; }
  return out;
}

function pruneInvoiceParams(query: InvoiceListQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (query.status) { out.status = query.status; }
  if (query.search && query.search.trim().length > 0) { out.search = query.search.trim(); }
  if (typeof query.page === 'number') { out.page = query.page; }
  if (typeof query.pageSize === 'number') { out.pageSize = query.pageSize; }
  return out;
}

function pruneDeliveryParams(query: DeliveryListQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (query.search && query.search.trim().length > 0) { out.search = query.search.trim(); }
  if (typeof query.page === 'number') { out.page = query.page; }
  if (typeof query.pageSize === 'number') { out.pageSize = query.pageSize; }
  return out;
}

function prunePatientParams(query: PatientListQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (query.search && query.search.trim().length > 0) { out.search = query.search.trim(); }
  if (typeof query.page === 'number') { out.page = query.page; }
  if (typeof query.pageSize === 'number') { out.pageSize = query.pageSize; }
  return out;
}
